import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { productsAPI } from '../api';
import { convertToKg as convertToKgOrLitre } from '../utils.js';
import ItemSearchBox from './ItemSearchBox';
import DirectRecipeCreate from './DirectRecipeCreate';
import ConfirmDialog from './ConfirmDialog';
import printRecipePDF from '../utils/printRecipePDF';

export default function RecipeTab({ recipe, productId, product, itemMaster, sfgLinks, onUpdated }) {
  const [editMode, setEditMode] = useState(false);
  const [recipeView, setRecipeView] = useState("fg"); // "fg" | "sfg-pct" | "sfg-rm"
  const [consolidatedIngredients, setConsolidatedIngredients] = useState([]);
  const [recipeVersions, setRecipeVersions] = useState([]);
  const [selectedVersion, setSelectedVersionR] = useState(null);
  const [versionDetail, setVersionDetail] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null); // sfg link pending removal
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('npd_user')); } catch { return null; } })();

  useEffect(() => {
    import('axios').then(m => {
      const API = m.default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      API.get('/products/'+productId+'/recipe-versions').then(r => setRecipeVersions(r.data));
    });
  }, [productId, recipe]);

  const loadVersion = (v) => {
    setSelectedVersionR(v.id);
    import('axios').then(m => {
      const API = m.default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      API.get('/recipe-versions/'+v.id).then(r => setVersionDetail(r.data));
    });
  };

  const submitForApproval = async () => {
    try {
      const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      await API.put(`/recipes/${recipe.id}/submit`);
      toast.success('Submitted for approval!');
      onUpdated();
    } catch(e) { toast.error('Failed to submit'); }
  };

  const approveRecipe = async () => {
    try {
      const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      await API.put(`/recipes/${recipe.id}/approve`, { approved_by: currentUser?.name || currentUser?.username });
      toast.success('Recipe approved!');
      onUpdated();
    } catch(e) { toast.error('Failed to approve'); }
  };

  const unlockRecipe = async () => {
    try {
      const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      await API.put(`/recipes/${recipe.id}/unlock`);
      toast.success('Recipe unlocked');
      onUpdated();
    } catch(e) { toast.error('Failed to unlock'); }
  };
  const [ingredients, setIngredients] = useState([]);
  const [batchSize, setBatchSize] = useState(recipe?.batch_size || "");
  const [batchUnit, setBatchUnit] = useState(recipe?.batch_unit || "kg");
  const [notes, setNotes] = useState('');
  const [hasBrine, setHasBrine] = useState(false);
  const [brineNotes, setBrineNotes] = useState('');
  const [brineIngredients, setBrineIngredients] = useState([]);
  const [brineForm, setBrineForm] = useState({ item_code: '', item_name: '', quantity: '', unit: '' });
  const [brineLitresPerKg, setBrineLitresPerKg] = useState('');

  const [processingInstructions, setProcessingInstructions] = useState('');
  const [preparationNotes, setPreparationNotes] = useState('');
  const [storageConditions, setStorageConditions] = useState('');
  const [shelfLife, setShelfLife] = useState('');
  const [addForm, setAddForm] = useState({ item_code: '', item_name: '', quantity: '', unit: '', unit_cost: '' });

  const startEdit = () => {
    setIngredients(recipe.ingredients.map(i => ({ ...i })));
    setNotes(recipe.notes || '');
    setHasBrine(recipe.has_brine === 1);
    setBrineNotes(recipe.brine_notes || '');
    setBrineIngredients((recipe.brineIngredients || []).map(i => ({ ...i })));
    setBrineLitresPerKg(recipe.brine_litres_per_kg || '');
    setProcessingInstructions(recipe.processing_instructions || '');
    setPreparationNotes(recipe.preparation_notes || '');
    setStorageConditions(recipe.storage_conditions || '');
    setShelfLife(recipe.shelf_life || '');
    setEditMode(true);
    setBatchSize(recipe.batch_size || "");
    setBatchUnit(recipe.batch_unit || "kg");
  };

  const handleItemSelect = (code) => {
    const item = itemMaster.find(i => i.item_code === code);
    if (item) setAddForm({ item_code: item.item_code, item_name: item.item_name, quantity: '', unit: item.unit || '', unit_cost: item.unit_cost || '' });
  };

  const addIngredient = () => {
    if (!addForm.item_code || !addForm.quantity) return;
    const qty = parseFloat(addForm.quantity) || 0;
    const unitCost = parseFloat(addForm.unit_cost || 0);
    const qtyInBase = convertToKgOrLitre(qty, addForm.unit);
    const total_cost = qtyInBase * unitCost;
    setIngredients([...ingredients, { ...addForm, total_cost, id: Date.now() }]);
    setAddForm({ item_code: '', item_name: '', quantity: '', unit: '', unit_cost: '' });
  };

  const updateUnit = (idx, unit) => {
    const copy = [...ingredients];
    copy[idx] = { ...copy[idx], unit };
    setIngredients(copy);
  };

  const updateQty = (idx, qty) => {
    const copy = [...ingredients];
    copy[idx] = { ...copy[idx], quantity: qty, total_cost: convertToKgOrLitre(parseFloat(qty), copy[idx].unit) * parseFloat(copy[idx].unit_cost || 0) };
    setIngredients(copy);
  };

  const updateUnitCost = (idx, unitCost) => {
    const copy = [...ingredients];
    const uc = parseFloat(unitCost) || 0;
    copy[idx] = { ...copy[idx], unit_cost: unitCost, total_cost: convertToKgOrLitre(parseFloat(copy[idx].quantity), copy[idx].unit) * uc };
    setIngredients(copy);
  };

  const removeIngredient = (idx) => setIngredients(ingredients.filter((_, i) => i !== idx));

  const handleBrineItemSelect = (code) => {
    const item = itemMaster.find(i => i.item_code === code);
    if (item) setBrineForm({ item_code: item.item_code, item_name: item.item_name, quantity: '', unit: item.unit || '' });
  };
  const addBrineIngredient = () => {
    if (!brineForm.item_code || !brineForm.quantity) return;
    setBrineIngredients([...brineIngredients, { ...brineForm, id: Date.now() }]);
    setBrineForm({ item_code: '', item_name: '', quantity: '', unit: '' });
  };
  const removeBrineIngredient = (idx) => setBrineIngredients(brineIngredients.filter((_, i) => i !== idx));

  const saveEdit = async () => {
    try {
      await import('../api').then(m => m.productsAPI.update && null); // ensure import
      const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      const userName = (() => { try { const u = JSON.parse(localStorage.getItem('npd_user')); return u?.name || u?.username || ''; } catch { return ''; } })();
      if (!(parseFloat(batchSize) > 0)) { toast.error('Batch size is required'); return; }
      await API.put(`/products/${productId}/recipe`, { notes, confirmed_by: userName, batch_size: parseFloat(batchSize)||null, batch_unit: batchUnit, ingredients, has_brine: hasBrine, brine_notes: brineNotes, brine_litres_per_kg: brineLitresPerKg, brineIngredients, processing_instructions: processingInstructions, preparation_notes: preparationNotes, storage_conditions: storageConditions, shelf_life: shelfLife });
      toast.success('Recipe updated!');
      setEditMode(false);
      onUpdated();
    } catch(e) { toast.error('Failed to save'); }
  };

  const total = ingredients.reduce((s, i) => s + (parseFloat(i.total_cost) || 0), 0);

  if (!recipe) return (
    <div className="card">
      <DirectRecipeCreate productId={productId} itemMaster={itemMaster} onCreated={onUpdated} />
    </div>
  );

  return (
    <div>
      {/* Recipe Version History */}
      {recipeVersions.length > 0 && (
        <div className="card mb-6" style={{ padding: '12px 16px' }}>
          <div className="card-title" style={{ margin: '0 0 8px' }}>Version History</div>
          <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
            <div onClick={() => { setSelectedVersionR(null); setVersionDetail(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: !selectedVersion ? 'var(--accent-dim)' : 'var(--bg-elevated)', border: '1px solid ' + (!selectedVersion ? 'var(--accent)' : 'var(--border)'), borderRadius: 8, fontSize: 11, cursor: 'pointer' }}>
              <span className="mono" style={{ color: 'var(--accent)', fontWeight: 700 }}>Current</span>
            </div>
            {recipeVersions.map(v => (
              <div key={v.id} onClick={() => loadVersion(v)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: selectedVersion === v.id ? 'var(--accent-dim)' : 'var(--bg-elevated)', border: '1px solid ' + (selectedVersion === v.id ? 'var(--accent)' : 'var(--border)'), borderRadius: 8, fontSize: 11, cursor: 'pointer' }}>
                <span className="mono" style={{ color: 'var(--accent)', fontWeight: 700 }}>v{v.version_number}</span>
                <span className="text-muted" style={{ fontSize: 10 }}>{v.confirmed_by || '—'}</span>
                <span className="text-muted" style={{ fontSize: 10 }}>{new Date(v.created_at).toLocaleDateString('en-AE')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Version detail view */}
      {versionDetail && (
        <div className="card mb-6">
          <div className="flex-between mb-4">
            <div>
              <div className="card-title" style={{ margin: 0 }}>Recipe v{versionDetail.version_number}</div>
              <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>Confirmed on {versionDetail.confirmed_at ? new Date(versionDetail.confirmed_at).toLocaleDateString('en-AE') : '—'} {versionDetail.confirmed_by ? 'by '+versionDetail.confirmed_by : ''}</div>
            </div>
            <div className="cost-value mono">AED {(versionDetail.total_cost||0).toLocaleString('en-AE', { maximumFractionDigits: 2 })}</div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: 'var(--bg-elevated)' }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, color: 'var(--text-muted)' }}>ITEM CODE</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, color: 'var(--text-muted)' }}>DESCRIPTION</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: 'var(--text-muted)' }}>QTY</th>
              <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>UNIT</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: 'var(--text-muted)' }}>UNIT COST</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: 'var(--text-muted)' }}>TOTAL</th>
            </tr></thead>
            <tbody>
              {versionDetail.ingredients.map((ing, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace', color: 'var(--accent)', fontWeight: 600, fontSize: 11 }}>{ing.item_code}</td>
                  <td style={{ padding: '8px 10px' }}>{ing.item_name}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{ing.quantity}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>{ing.unit}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{(ing.unit_cost||0).toFixed(2)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{(ing.total_cost||0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    <div className="card">
      <div className="flex-between mb-4">
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div className="card-title" style={{ margin: 0 }}>Confirmed Recipe</div>
            {sfgLinks && sfgLinks.length > 0 && (
              <div style={{ display:'flex', gap:4 }}>
                <button onClick={() => setRecipeView('fg')} className={`btn btn-sm ${recipeView==='fg' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize:10 }}>FG Recipe</button>
                <button onClick={() => setRecipeView('sfg-pct')} className={`btn btn-sm ${recipeView==='sfg-pct' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize:10 }}>SFG %</button>
                <button onClick={() => { setRecipeView('sfg-rm'); import('axios').then(m => m.default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' }).get('/recipes/'+recipe.id+'/consolidated').then(r => setConsolidatedIngredients(r.data.ingredients||[]))); }} className={`btn btn-sm ${recipeView==='sfg-rm' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize:10 }}>RM View</button>
              </div>
            )}
          </div>
          <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>Confirmed on {new Date(recipe.confirmed_at).toLocaleDateString('en-AE')}</div>
            {recipe.batch_size && (() => { const rmKg = (recipe.ingredients||[]).reduce((s, i) => { const u = (i.unit||"kg").toLowerCase(); return s + (["g","gm","gram","grams","ml"].includes(u) ? (parseFloat(i.quantity)||0)/1000 : (parseFloat(i.quantity)||0)); }, 0); const yieldPct = rmKg > 0 ? ((recipe.batch_size/rmKg)*100).toFixed(1) : null; return (<div style={{ display:"flex", gap:12, marginTop:6, flexWrap:"wrap" }}><div style={{ background:"var(--accent-dim)", borderRadius:6, padding:"4px 10px", fontSize:12 }}><span style={{ color:"var(--text-muted)", fontSize:10 }}>BATCH SIZE </span><span style={{ color:"var(--accent)", fontWeight:700 }}>{recipe.batch_size} {recipe.batch_unit||"kg"}</span></div>{rmKg > 0 && <div style={{ background:"var(--bg-elevated)", borderRadius:6, padding:"4px 10px", fontSize:12 }}><span style={{ color:"var(--text-muted)", fontSize:10 }}>TOTAL RM </span><span style={{ fontWeight:700 }}>{rmKg.toFixed(3)} kg</span></div>}{yieldPct && <div style={{ background: parseFloat(yieldPct) < 80 ? "#fff3e0" : "#f0faf0", borderRadius:6, padding:"4px 10px", fontSize:12 }}><span style={{ color:"var(--text-muted)", fontSize:10 }}>YIELD </span><span style={{ fontWeight:700, color: parseFloat(yieldPct) < 80 ? "#e67e00" : "#2e7d32" }}>{yieldPct}%</span></div>}</div>); })()}
        </div>
        <div className="flex gap-2" style={{ alignItems: 'center' }}>
          <div className="cost-value mono" style={{ fontSize: 16 }}>Total: AED {(editMode ? total : recipe.total_cost || 0).toLocaleString('en-AE', { maximumFractionDigits: 2 })}</div>
          {!editMode
            ? <>
                {sfgLinks && sfgLinks.length > 0 ? (
                  <>
                    <button className="btn btn-secondary btn-sm" onClick={async () => {
                      try {
                        const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
                        const r = await API.get('/recipes/'+recipe.id+'/consolidated');
                        printRecipePDF(recipe, product, r.data.ingredients || [], sfgLinks, 'consolidated');
                      } catch { toast.error('Failed to load RM data'); }
                    }}>⬇ PDF (Consolidated)</button>
                    <button className="btn btn-secondary btn-sm" onClick={async () => {
                      try {
                        const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
                        const r = await API.get('/recipes/'+recipe.id+'/consolidated');
                        printRecipePDF(recipe, product, r.data.ingredients || [], sfgLinks, 'separate');
                      } catch { toast.error('Failed to load RM data'); }
                    }}>⬇ PDF (By SFG)</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => {
                      // SFG-only view: each SFG as one line with its %
                      const sfgOnlyIngredients = sfgLinks.map(s => ({
                        item_code: s.sfg_code || 'SFG',
                        item_name: s.sfg_name,
                        quantity: (s.percentage || 0) / 100,
                        unit: 'kg',
                        total_cost: 0
                      }));
                      printRecipePDF(recipe, product, sfgOnlyIngredients, sfgLinks, 'consolidated');
                    }}>⬇ PDF (SFG Only)</button>
                  </>
                ) : (
                  <button className="btn btn-secondary btn-sm" onClick={() => product && printRecipePDF(recipe, product)}>⬇ PDF</button>
                )}
                {recipe.approval_status === 'approved' ? (
                  <>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', background:'var(--accent-dim)', borderRadius:20, fontSize:11, color:'var(--accent)' }}>✓ Approved by {recipe.approved_by}</span>
                    {(currentUser?.role==='approver'||currentUser?.username==='admin') && <button className="btn btn-secondary btn-sm" onClick={unlockRecipe}>🔓 Unlock</button>}
                  </>
                ) : recipe.approval_status === 'pending' ? (
                  <>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', background:'var(--amber-dim)', borderRadius:20, fontSize:11, color:'var(--amber)' }}>⏳ Pending Approval</span>
                    {(currentUser?.role==='approver'||currentUser?.username==='admin') && <button className="btn btn-primary btn-sm" onClick={approveRecipe}>✓ Approve</button>}
                  </>
                ) : (
                  <>
                    {recipe.rejection_reason && <span style={{ fontSize:11, color:'var(--red)', marginRight:4 }}>Rejected: {recipe.rejection_reason}</span>}
                    <button className="btn btn-secondary btn-sm" onClick={submitForApproval}>Submit for Approval</button>
                    <button className="btn btn-secondary btn-sm" onClick={startEdit}>✏ Edit Recipe</button>
                  </>
                )}
              </>
            : <><button className="btn btn-secondary btn-sm" onClick={() => setEditMode(false)}>Cancel</button><button className="btn btn-primary btn-sm" onClick={saveEdit}>Save</button></>
          }
        </div>
      </div>

      {recipe && !(recipe.batch_size > 0) && (
        <div style={{ background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: 8, padding: '8px 14px', marginBottom: 10, fontSize: 12, fontWeight: 600, color: '#e65100' }}>
          ⚠ Batch size missing — costs per kg and yields can't be computed. Edit the recipe and set it.
        </div>
      )}
      {editMode ? (
        <>
          {/* SFG-based FG: show SFG % editor instead of RM ingredients */}
          {sfgLinks && sfgLinks.length > 0 ? (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>This is an FG product with SFGs. Edit each SFG's percentage below.</div>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead><tr style={{ background:'var(--bg-elevated)' }}>
                  <th style={{ padding:'8px 10px', textAlign:'left', fontSize:11 }}>SFG NAME</th>
                  <th style={{ padding:'8px 10px', textAlign:'right', fontSize:11, width:100 }}>% IN FG</th>
                  <th style={{ padding:'8px 10px', textAlign:'center', fontSize:11, width:80 }}>ACTION</th>
                </tr></thead>
                <tbody>
                  {sfgLinks.map(s => (
                    <tr key={s.sfg_product_id} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'8px 10px' }}>
                        <div style={{ fontWeight:600 }}>{s.sfg_name}</div>
                        <div style={{ fontSize:11, fontFamily:'monospace', color:'var(--accent)' }}>{s.sfg_code}</div>
                      </td>
                      <td style={{ padding:'8px 10px', textAlign:'right' }}>
                        <input type="number" defaultValue={s.percentage} min="0" max="100" step="0.1"
                          style={{ width:70, padding:'4px 8px', textAlign:'right', border:'1px solid var(--border)', borderRadius:4 }}
                          onBlur={async e => {
                            const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
                            await API.put(`/recipes/sfg-links/${s.id}`, { percentage: parseFloat(e.target.value)||0, notes: s.notes||'' });
                            toast.success('% updated');
                            onUpdated && onUpdated();
                            onUpdated && onUpdated();
                          }} />
                      </td>
                      <td style={{ padding:'8px 10px', textAlign:'center' }}>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize:10 }} onClick={() => window.location.href='/products/'+s.sfg_product_id}>Edit SFG →</button>
                        <button className="btn btn-danger btn-sm" style={{ fontSize:10, marginLeft:6 }} onClick={() => setRemoveTarget(s)}>✕ Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(() => {
                const tot = (sfgLinks || []).reduce((s, x) => s + (parseFloat(x.percentage) || 0), 0);
                const ok = Math.abs(tot - 100) < 0.05;
                return (
                  <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                                background: ok ? '#e8f5e9' : '#fff3e0', color: ok ? '#2e7d32' : '#e65100',
                                border: '1px solid ' + (ok ? '#a5d6a7' : '#ffcc80') }}>
                    Total: {tot.toFixed(2)}% {ok ? '✓' : '— should equal 100%'}
                  </div>
                );
              })()}
              <div style={{ marginTop:12, display:'flex', gap:8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditMode(false)}>Cancel</button>
              </div>
            </div>
          ) : (
          <>
          {/* Add ingredient row */}
          <div style={{ padding: '12px 14px', background: 'var(--bg-elevated)', borderRadius: 8, marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 10 }}>Add Ingredient</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 2, minWidth: 180 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>ITEM</label>
                <ItemSearchBox value={addForm.item_code} onSelect={handleItemSelect} itemMaster={itemMaster} />
              </div>
              <div style={{ flex: 1, minWidth: 80 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>QTY</label>
                <input type="number" placeholder="0" value={addForm.quantity} onChange={e => setAddForm({ ...addForm, quantity: e.target.value })} />
              </div>
              <div style={{ minWidth: 80 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>UOM</label>
                <select value={addForm.unit} onChange={e => setAddForm({ ...addForm, unit: e.target.value })} style={{ width: 80, fontSize: 12 }}>
                  <option value="">—</option>
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="l">l</option>
                  <option value="ml">ml</option>
                  <option value="nos">nos</option>
                  <option value="pcs">pcs</option>
                </select>
              </div>

              <button className="btn btn-primary btn-sm" onClick={addIngredient}>+ Add</button>
            </div>
          </div>

          <div className="form-group mb-4">
            <label>Recipe Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} />
          </div>

          {/* Brine toggle */}
          <div style={{ padding: '12px 14px', background: 'var(--bg-elevated)', borderRadius: 8, marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', textTransform: 'none', letterSpacing: 'normal', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
              <input type="checkbox" checked={hasBrine} onChange={e => setHasBrine(e.target.checked)} style={{ width: 'auto', accentColor: 'var(--accent)' }} />
              This product uses a Brine Solution
            </label>
          </div>

          {/* Processing & Storage fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div className="form-group">
              <label>Processing Instructions</label>
              <textarea rows={4} placeholder="e.g. Roast at 160°C for 12 mins..." value={processingInstructions} onChange={e => setProcessingInstructions(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Preparation Notes</label>
              <textarea rows={4} placeholder="e.g. Ensure all equipment is dry..." value={preparationNotes} onChange={e => setPreparationNotes(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Storage Conditions</label>
              <textarea rows={2} placeholder="e.g. Store below 25°C in dry place..." value={storageConditions} onChange={e => setStorageConditions(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Shelf Life</label>
              <input placeholder="e.g. 6 months from production date" value={shelfLife} onChange={e => setShelfLife(e.target.value)} />
            </div>
          </div>

          {hasBrine && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div className="card-title" style={{ marginBottom: 12 }}>Brine Recipe</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
                <div style={{ flex: 2, minWidth: 180 }}>
                  <label>Select Item</label>
                  <select value={brineForm.item_code} onChange={e => handleBrineItemSelect(e.target.value)} style={{ width: '100%' }}>
                    <option value="">— Select item —</option>
                    {itemMaster.map(i => <option key={i.id} value={i.item_code}>{i.item_code} — {i.item_name}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 70 }}>
                  <label>Qty</label>
                  <input type="number" placeholder="0" value={brineForm.quantity} onChange={e => setBrineForm({ ...brineForm, quantity: e.target.value })} />
                </div>
                <div style={{ flex: 1, minWidth: 60 }}>
                  <label>Unit</label>
                  <input placeholder="kg/L" value={brineForm.unit} onChange={e => setBrineForm({ ...brineForm, unit: e.target.value })} />
                </div>

                <button className="btn btn-primary btn-sm" onClick={addBrineIngredient}>+ Add</button>
              </div>
              {brineIngredients.length > 0 && (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Code</th><th>Name</th><th>Qty</th><th>Unit</th><th>Litres/kg RM</th><th></th></tr></thead>
                    <tbody>
                      {brineIngredients.map((bi, idx) => (
                        <tr key={bi.id || idx}>
                          <td className="mono">{bi.item_code}</td>
                          <td>{bi.item_name}</td>
                          <td className="mono">{bi.quantity}</td>
                          <td>{bi.unit}</td>
                          <td className="mono">{bi.litres_per_kg}</td>
                          <td><button className="btn btn-danger btn-sm" onClick={() => removeBrineIngredient(idx)}>×</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <div className="form-group">
                  <label>Litres of Brine per kg of RM</label>
                  <input type="number" step="0.01" placeholder="e.g. 0.50" value={brineLitresPerKg} onChange={e => setBrineLitresPerKg(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Brine Notes</label>
                  <textarea placeholder="Brine preparation notes..." value={brineNotes} onChange={e => setBrineNotes(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          <div className="table-wrap">
            <table>
              <thead><tr><th>Code</th><th>Name</th><th>Qty</th><th>Unit</th><th>Unit Cost</th><th className="text-right">Total</th><th></th></tr></thead>
              <tbody>
                {ingredients.map((ing, idx) => (
                  <tr key={ing.id || idx}>
                    <td className="mono">{ing.item_code}</td>
                    <td>{ing.item_name}</td>
                    <td><input type="number" value={ing.quantity} onChange={e => updateQty(idx, e.target.value)} style={{ width: 80, padding: '4px 8px' }} /></td>
                    <td><select value={ing.unit} onChange={e => updateUnit(idx, e.target.value)} style={{ width: 65, fontSize: 12, padding: '3px 4px' }}>
                      <option value="kg">kg</option>
                      <option value="g">g</option>
                      <option value="l">l</option>
                      <option value="ml">ml</option>
                      <option value="nos">nos</option>
                      <option value="pcs">pcs</option>
                    </select></td>
                    <td><input type="number" step="0.01" value={ing.unit_cost || 0} onChange={e => updateUnitCost(idx, e.target.value)} style={{ width: 80, padding: "4px 8px" }} /></td>
                    <td className="text-right cost-value">AED {(ing.total_cost || 0).toLocaleString('en-AE', { maximumFractionDigits: 2 })}</td>
                    <td><button className="btn btn-danger btn-sm" onClick={() => removeIngredient(idx)}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--accent-dim)', borderRadius: 8, fontFamily: 'DM Mono, monospace', fontSize: 13 }}>
            <span className="text-accent">Total: AED {total.toLocaleString('en-AE', { maximumFractionDigits: 2 })}</span>
            <span className="text-muted" style={{ marginLeft: 16 }}>{ingredients.length} ingredients</span>
            {(() => { const rmKg = ingredients.reduce((s, i) => { const u = (i.unit||'kg').toLowerCase(); return s + (['g','gm','gram','grams','ml'].includes(u) ? (parseFloat(i.quantity)||0)/1000 : (parseFloat(i.quantity)||0)); }, 0); return rmKg > 0 ? <span className="text-muted" style={{ marginLeft: 16 }}>Total RM: {rmKg.toFixed(3)} kg</span> : null; })()}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'flex-end' }}>
            <div className="form-group" style={{ margin: 0, flex: 1 }}>
              <label>Batch Size (finished product)</label>
              <input type="number" placeholder="e.g. 100 (required)" value={batchSize} onChange={e => setBatchSize(e.target.value)}
                style={{ borderColor: !(parseFloat(batchSize) > 0) ? 'var(--red, #c62828)' : undefined }} />
            </div>
            <div className="form-group" style={{ margin: 0, width: 80 }}>
              <label>Unit</label>
              <select value={batchUnit} onChange={e => setBatchUnit(e.target.value)}>
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="l">l</option>
              </select>
            </div>
            {batchSize && (() => { const rmKg = ingredients.reduce((s, i) => { const u = (i.unit||'kg').toLowerCase(); return s + (['g','gm','gram','grams','ml'].includes(u) ? (parseFloat(i.quantity)||0)/1000 : (parseFloat(i.quantity)||0)); }, 0); const yld = rmKg > 0 ? ((parseFloat(batchSize)/rmKg)*100).toFixed(1) : null; return yld ? <div style={{ paddingBottom: 8, fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>Yield: {yld}%</div> : null; })()}
          </div>
          </>
          )}
        </>
      ) : (
        <>
          {recipe.notes && <div style={{ padding: '12px 14px', background: 'var(--bg-elevated)', borderRadius: 8, marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>{recipe.notes}</div>}

          {/* SFG % View */}
          {recipeView === 'sfg-pct' && sfgLinks && sfgLinks.length > 0 && (
            <div>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead><tr style={{ background:'var(--bg-elevated)' }}>
                  <th style={{ padding:'8px 10px', textAlign:'left', fontSize:11, color:'var(--text-muted)' }}>SFG CODE</th>
                  <th style={{ padding:'8px 10px', textAlign:'left', fontSize:11, color:'var(--text-muted)' }}>SFG NAME</th>
                  <th style={{ padding:'8px 10px', textAlign:'right', fontSize:11, color:'var(--text-muted)' }}>%</th>
                  <th style={{ padding:'8px 10px', textAlign:'center', fontSize:11, color:'var(--text-muted)' }}>RECIPE</th>
                </tr></thead>
                <tbody>
                  {sfgLinks.map(s => (
                    <tr key={s.sfg_product_id} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'8px 10px', fontFamily:'monospace', fontSize:11, color:'var(--accent)', fontWeight:700 }}>{s.sfg_code}</td>
                      <td style={{ padding:'8px 10px', fontWeight:500 }}>{s.sfg_name}</td>
                      <td style={{ padding:'8px 10px', textAlign:'right', fontWeight:700 }}>{s.percentage}%</td>
                      <td style={{ padding:'8px 10px', textAlign:'center' }}>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize:10 }} onClick={() => window.location.href='/products/'+s.sfg_product_id}>View Recipe →</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* SFG RM Exploded View */}
          {recipeView === 'sfg-rm' && (
            <div>
              {consolidatedIngredients.length > 0 ? (
                <div>
                  {(() => {
                    // Group by source (SFG name)
                    const groups = {};
                    consolidatedIngredients.forEach(i => {
                      const src = i.source || 'FG Direct';
                      if (!groups[src]) groups[src] = [];
                      groups[src].push(i);
                    });
                    return Object.entries(groups).map(([src, items]) => (
                      <div key={src} style={{ marginBottom: 16 }}>
                        <div style={{ padding:'6px 10px', background:'var(--accent)', color:'white', fontWeight:700, fontSize:12, borderRadius:'6px 6px 0 0' }}>
                          {src}
                          <span style={{ marginLeft:8, fontWeight:400, fontSize:11, opacity:0.8 }}>
                            AED {items.reduce((s,i) => s+(i.total_cost||0), 0).toFixed(2)}
                          </span>
                        </div>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                          <thead><tr style={{ background:'var(--bg-elevated)' }}>
                            <th style={{ padding:'6px 10px', textAlign:'left', fontSize:10, color:'var(--text-muted)' }}>ITEM CODE</th>
                            <th style={{ padding:'6px 10px', textAlign:'left', fontSize:10, color:'var(--text-muted)' }}>INGREDIENT</th>
                            <th style={{ padding:'6px 10px', textAlign:'right', fontSize:10, color:'var(--text-muted)' }}>QTY</th>
                            <th style={{ padding:'6px 10px', textAlign:'center', fontSize:10, color:'var(--text-muted)' }}>UNIT</th>
                            <th style={{ padding:'6px 10px', textAlign:'right', fontSize:10, color:'var(--text-muted)' }}>COST</th>
                          </tr></thead>
                          <tbody>
                            {items.map((i, idx) => (
                              <tr key={idx} style={{ borderBottom:'1px solid var(--border)' }}>
                                <td style={{ padding:'6px 10px', fontFamily:'monospace', fontSize:10, color:'var(--accent)', fontWeight:600 }}>{i.item_code}</td>
                                <td style={{ padding:'6px 10px' }}>{i.item_name}</td>
                                <td style={{ padding:'6px 10px', textAlign:'right' }}>{i.quantity}</td>
                                <td style={{ padding:'6px 10px', textAlign:'center' }}>{i.unit}</td>
                                <td style={{ padding:'6px 10px', textAlign:'right', fontWeight:600 }}>AED {(i.total_cost||0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ));
                  })()}
                </div>
              ) : <div className="empty-state"><p>Click "RM View" to load consolidated ingredients from SFGs.</p></div>}
            </div>
          )}

          {/* FG Recipe View */}
          {recipeView === 'fg' && <div className="table-wrap">
            <table>
              <thead><tr><th>Item Code</th><th>Ingredient</th><th>Qty</th><th>Unit</th><th>Unit Cost</th><th className="text-right">Total Cost</th></tr></thead>
              <tbody>
                {recipe.ingredients.map(i => (
                  <tr key={i.id}>
                    <td className="mono">{i.item_code}</td>
                    <td>{i.item_name || '—'}</td>
                    <td className="mono">{i.quantity}</td>
                    <td>{i.unit}</td>
                    <td className="mono">AED {(i.unit_cost || 0).toLocaleString('en-AE', { maximumFractionDigits: 2 })}</td>
                    <td className="text-right cost-value">AED {(i.total_cost || 0).toLocaleString('en-AE', { maximumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
        </>
      )}
    </div>
      <ConfirmDialog
        open={!!removeTarget}
        title="Remove SFG"
        danger
        confirmLabel="Remove now"
        message={removeTarget ? `Remove "${removeTarget.sfg_name}" from this FG recipe?` : ''}
        detail="This applies immediately. The SFG can be re-added later if needed."
        onCancel={() => setRemoveTarget(null)}
        onConfirm={async () => {
          const s = removeTarget; setRemoveTarget(null);
          try {
            const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
            await API.delete('/recipes/sfg-links/' + s.id);
            toast.success('SFG removed from recipe');
            onUpdated && onUpdated();
          } catch (e) { toast.error(e.response?.data?.error || 'Remove failed'); }
        }}
      />
    </div>
  );
}
